/**
 * DAFNE Default Settings
 *
 * Default user settings and configuration values.
 * These are the initial values when a user first sets up the app.
 *
 * Last reviewed: February 2026
 */

import { BG_THRESHOLDS } from "./thresholds";
import { CORRECTION_CONFIG, DEFAULT_CP_RATIOS } from "./insulin";
import { DEFAULT_MEAL_TIMES } from "./time-periods";
import { CorrectionStackingMode } from "../../types";

// =============================================================================
// DEFAULT USER SETTINGS
// =============================================================================

export const USER_DEFAULTS = {
  /** Default blood glucose units */
  BG_UNITS: "mmol" as const,

  /** Default carbohydrate units */
  CARB_UNITS: "grams" as const,

  /** Default target BG range */
  TARGET_BG_LOW: BG_THRESHOLDS.TARGET_LOW_DEFAULT,
  TARGET_BG_HIGH: BG_THRESHOLDS.TARGET_HIGH_DEFAULT,

  /** Default carb:insulin ratios - null = user must configure */
  CP_RATIOS: { ...DEFAULT_CP_RATIOS },

  /** Default correction factor (ISF) - null = user must configure */
  CORRECTION_FACTOR: CORRECTION_CONFIG.DEFAULT_CORRECTION_FACTOR,

  /** Default correction stacking mode - DAFNE recommends no stacking */
  CORRECTION_STACKING: "no-stacking" as CorrectionStackingMode,

  /** Default insulin delivery method */
  INSULIN_DELIVERY: "mdi" as const,

  /** Default dose increment (half-unit pens common in Australia) */
  DOSE_INCREMENT: 0.5,

  /** Default quick-acting insulin type */
  QA_INSULIN_TYPE: "novorapid" as const,

  /** Default background insulin type */
  BI_INSULIN_TYPE: "lantus" as const,

  /** Default background insulin frequency */
  BI_FREQUENCY: "once" as const,

  /** Default meal times */
  MEAL_TIMES: { ...DEFAULT_MEAL_TIMES },

  /** Default target profile - standard unless clinician advises otherwise */
  TARGET_PROFILE: "standard" as const,

  /** Default data sharing consent - none until user explicitly opts in */
  DATA_SHARING: {
    allowDafneDataSharing: "none" as const,
    allowCgmDataSharing: "none" as const,
  },
};

// =============================================================================
// SETTINGS VALIDATION
// =============================================================================

/**
 * Required settings that user must configure from their DAFNE course.
 * These have no safe default values - wrong values could be dangerous.
 */
export const REQUIRED_SETTINGS = [
  "cpRatios.B",
  "cpRatios.L",
  "cpRatios.EM",
  "correctionFactor",
] as const;

/**
 * Check if all required DAFNE settings have been configured.
 * Returns list of missing settings, or empty array if all configured.
 */
export function getMissingSettings(settings: {
  cpRatios?: Record<string, number | null>;
  correctionFactor?: number | null;
}): string[] {
  const missing: string[] = [];

  if (!settings.cpRatios?.B) missing.push("Breakfast CP ratio");
  if (!settings.cpRatios?.L) missing.push("Lunch CP ratio");
  if (!settings.cpRatios?.EM) missing.push("Evening Meal CP ratio");
  if (!settings.correctionFactor) missing.push("Correction Factor (ISF)");

  return missing;
}

/**
 * Check if settings are complete enough to calculate insulin suggestions.
 */
export function areSettingsComplete(settings: {
  cpRatios?: Record<string, number | null>;
  correctionFactor?: number | null;
}): boolean {
  return getMissingSettings(settings).length === 0;
}

// =============================================================================
// PERIOD TAG METADATA
// =============================================================================

export const PERIOD_TAG_INFO = {
  B: { label: "Breakfast", sortOrder: 1, ratioFrom: "B" as const },
  L: { label: "Lunch", sortOrder: 3, ratioFrom: "L" as const },
  EM: { label: "Evening Meal", sortOrder: 5, ratioFrom: "EM" as const },
  "after-B": {
    label: "After Breakfast",
    sortOrder: 2,
    ratioFrom: "B" as const,
  },
  "after-L": { label: "After Lunch", sortOrder: 4, ratioFrom: "L" as const },
  "after-EM": {
    label: "After Evening Meal",
    sortOrder: 6,
    ratioFrom: "EM" as const,
  },
  BT: { label: "Bedtime", sortOrder: 7, ratioFrom: "BT" as const },
  "3AM": { label: "3am", sortOrder: 0, ratioFrom: "3AM" as const },
} as const;

// =============================================================================
// FULL DEFAULT SETTINGS OBJECT
// =============================================================================

/**
 * Complete default settings object for new users.
 * This matches the UserSettings interface structure.
 */
export const DEFAULT_SETTINGS = {
  id: "settings",
  bgUnits: USER_DEFAULTS.BG_UNITS,
  carbUnits: USER_DEFAULTS.CARB_UNITS,
  targetBgLow: USER_DEFAULTS.TARGET_BG_LOW,
  targetBgHigh: USER_DEFAULTS.TARGET_BG_HIGH,
  cpRatios: { ...USER_DEFAULTS.CP_RATIOS },
  correctionFactor: USER_DEFAULTS.CORRECTION_FACTOR,
  correctionStacking: USER_DEFAULTS.CORRECTION_STACKING,
  insulinDelivery: USER_DEFAULTS.INSULIN_DELIVERY,
  doseIncrement: USER_DEFAULTS.DOSE_INCREMENT,
  qaInsulinType: USER_DEFAULTS.QA_INSULIN_TYPE,
  biInsulinType: USER_DEFAULTS.BI_INSULIN_TYPE,
  biFrequency: USER_DEFAULTS.BI_FREQUENCY,
  mealTimes: { ...USER_DEFAULTS.MEAL_TIMES },
  targetProfile: USER_DEFAULTS.TARGET_PROFILE,
  dataSharing: { ...USER_DEFAULTS.DATA_SHARING },
} as const;
